/* eslint-disable no-plusplus */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import camelCaseKeys from 'camelcase-keys'
import snakeCaseKeys from 'snakecase-keys'

import { acquireAccessToken } from '../auth/auth'
import { getEnvs } from './env'

const basePath = '/aigo'
const API_LOG_PREFIX = '[API]'

const {
  apiUrl,
  apiBasicAuthType,
  apiBasicAuthTypeUsername,
  apiBasicAuthTypePassword,
} = getEnvs()

export const BASE_URL = `${apiUrl}${basePath}`

// Helper to calculate FormData size
const getFormDataSize = (
  formData: FormData
): {
  totalSize: number
  fileCount: number
  fileSizes: Record<string, number>
} => {
  let totalSize = 0
  let fileCount = 0
  const fileSizes: Record<string, number> = {}

  formData.forEach((value: FormDataEntryValue, key: string) => {
    if (value instanceof File) {
      totalSize += value.size
      fileCount++
      fileSizes[`${key}:${value.name}`] = value.size
    } else if (typeof value === 'string') {
      totalSize += new Blob([value]).size
    }
  })

  return { totalSize, fileCount, fileSizes }
}

// Helper to format bytes
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`
}

// Track active requests for debugging
const activeRequests = new Map<
  string,
  { url: string; startTime: number; method: string }
>()

const generateRequestId = () => Math.random().toString(36).substring(2, 10)

const logRequestStart = (
  requestId: string,
  method: string,
  url: string,
  payloadInfo?: object
) => {
  activeRequests.set(requestId, { url, startTime: Date.now(), method })
  console.log(API_LOG_PREFIX, `[${requestId}] ${method} ${url} STARTED`, {
    timestamp: new Date().toISOString(),
    activeRequests: activeRequests.size,
    ...payloadInfo,
  })
}

const logRequestEnd = (
  requestId: string,
  success: boolean,
  details?: object
) => {
  const request = activeRequests.get(requestId)
  const duration = request ? Date.now() - request.startTime : 0
  activeRequests.delete(requestId)

  const level = success ? 'log' : 'error'
  console[level](
    API_LOG_PREFIX,
    `[${requestId}] ${request?.method} ${request?.url} ${
      success ? 'SUCCESS' : 'FAILED'
    }`,
    {
      duration: `${duration}ms`,
      timestamp: new Date().toISOString(),
      activeRequests: activeRequests.size,
      ...details,
    }
  )
}

// Periodic log of active requests (for debugging stuck requests)
setInterval(() => {
  if (activeRequests.size > 0) {
    const now = Date.now()
    const requests = Array.from(activeRequests.entries()).map(([id, req]) => ({
      id,
      url: req.url,
      method: req.method,
      elapsed: `${Math.round((now - req.startTime) / 1000)}s`,
    }))
    console.log(API_LOG_PREFIX, 'Active requests:', {
      count: activeRequests.size,
      requests,
    })
  }
}, 30000) // Log every 30 seconds if there are active requests

axios.interceptors.request.use(
  (config) =>
    new Promise((resolve, reject) => {
      if (apiBasicAuthType) {
        resolve({
          ...config,
          auth: {
            username: apiBasicAuthTypeUsername,
            password: apiBasicAuthTypePassword,
          },
        })
      } else {
        acquireAccessToken()
          .then((tokenResp) => {
            resolve({
              ...config,
              headers: {
                ...config.headers,
                Authorization: tokenResp && `Bearer ${tokenResp.accessToken}`,
              }, // todo: handle tokenResp=void
            })
          })
          .catch((error) => {
            reject(error)
          })
      }
    }),
  (error: AxiosError) => {
    Promise.reject(error)
  }
)

export const get = async <T>(
  url: string,
  options?: AxiosRequestConfig,
  zipType?: boolean // file to download is a zip file
): Promise<T> =>
  new Promise((resolve, reject) => {
    const requestId = generateRequestId()
    const fullUrl = `${apiUrl}${basePath}${url}`

    logRequestStart(requestId, 'GET', fullUrl)

    axios
      .get(fullUrl, options)
      .then((res: AxiosResponse) => {
        logRequestEnd(requestId, true, { status: res.status })
        if (zipType) {
          resolve(res.data)
        } else {
          resolve(camelCaseKeys(res.data, { deep: true }))
        }
      })
      .catch((error: AxiosError) => {
        const errorDetails: Record<string, any> = {
          message: error.message,
          code: error.code,
          isNetworkError: error.message === 'Network Error',
        }
        if (error.response) {
          errorDetails.responseStatus = error.response.status
          errorDetails.responseData = error.response.data
        } else if (error.request) {
          errorDetails.noResponse = true
          if (error.message === 'Network Error') {
            errorDetails.possibleCause =
              'Server may have run out of file descriptors'
          }
        }
        logRequestEnd(requestId, false, errorDetails)
        reject(error)
      })
  })

export const post = async <T>(
  url: string,
  payload: object,
  options?: AxiosRequestConfig,
  payloadIsNotAnObject?: boolean
): Promise<T> =>
  new Promise((resolve, reject) => {
    const p = payloadIsNotAnObject ? payload : snakeCaseKeys(payload)
    const requestId = generateRequestId()
    const fullUrl = `${apiUrl}${basePath}${url}`

    // Calculate payload info for logging
    let payloadInfo: object = {}
    if (payload instanceof FormData) {
      const { totalSize, fileCount, fileSizes } = getFormDataSize(payload)
      payloadInfo = {
        payloadType: 'FormData',
        totalSize: formatBytes(totalSize),
        totalSizeBytes: totalSize,
        fileCount,
        // Only log individual file sizes if there are few files
        ...(fileCount <= 10
          ? { fileSizes }
          : { firstFiles: Object.entries(fileSizes).slice(0, 5) }),
      }
    } else {
      payloadInfo = {
        payloadType: 'JSON',
        payloadKeys: Object.keys(payload),
      }
    }

    logRequestStart(requestId, 'POST', fullUrl, payloadInfo)

    axios
      .post(fullUrl, p, options)
      .then((res: AxiosResponse) => {
        logRequestEnd(requestId, true, {
          status: res.status,
          statusText: res.statusText,
        })
        resolve(camelCaseKeys(res.data, { deep: true }))
      })
      .catch((error: AxiosError) => {
        // Detailed error logging for network issues
        const errorDetails: Record<string, any> = {
          message: error.message,
          code: error.code,
          isNetworkError: error.message === 'Network Error',
          isTimeout: error.code === 'ECONNABORTED',
        }

        if (error.response) {
          // Server responded with error status
          errorDetails.responseStatus = error.response.status
          errorDetails.responseStatusText = error.response.statusText
          errorDetails.responseData = error.response.data
        } else if (error.request) {
          // Request was made but no response received
          errorDetails.noResponse = true
          errorDetails.requestDetails = 'Request sent but no response received'
          // This is likely the "Too many open files" case on the server
          if (error.message === 'Network Error') {
            errorDetails.possibleCause =
              'Server may have run out of file descriptors (Too many open files)'
            errorDetails.suggestion =
              'Check server ulimit settings and connection handling'
          }
        }

        logRequestEnd(requestId, false, errorDetails)
        reject(error)
      })
  })

export const update = async <T>(
  url: string,
  payload: object,
  options?: AxiosRequestConfig,
  payloadIsNotAnObject?: boolean
): Promise<T> =>
  new Promise((resolve, reject) => {
    const p = payloadIsNotAnObject ? payload : snakeCaseKeys(payload)
    const requestId = generateRequestId()
    const fullUrl = `${apiUrl}${basePath}${url}`

    logRequestStart(requestId, 'PATCH', fullUrl, {
      payloadKeys: Object.keys(payload),
    })

    axios
      .patch(fullUrl, p, options)
      .then((res: AxiosResponse) => {
        logRequestEnd(requestId, true, { status: res.status })
        resolve(camelCaseKeys(res.data, { deep: true }))
      })
      .catch((error: AxiosError) => {
        logRequestEnd(requestId, false, {
          message: error.message,
          code: error.code,
          responseStatus: error.response?.status,
        })
        reject(error)
      })
  })

export const del = async <T>(
  url: string,
  options?: AxiosRequestConfig
): Promise<T> =>
  new Promise((resolve, reject) => {
    const requestId = generateRequestId()
    const fullUrl = `${apiUrl}${basePath}${url}`

    logRequestStart(requestId, 'DELETE', fullUrl)

    axios
      .delete(fullUrl, options)
      .then((res: AxiosResponse) => {
        logRequestEnd(requestId, true, { status: res.status })
        resolve(camelCaseKeys(res.data, { deep: true }))
      })
      .catch((error: AxiosError) => {
        logRequestEnd(requestId, false, {
          message: error.message,
          code: error.code,
          responseStatus: error.response?.status,
        })
        reject(error)
      })
  })
